import { ContractingFormType } from "features/contracting/enums/contractingFormType";
import {
  getBoolText,
  getContractingAddressName,
  getFormattedDate,
  getTypedStepValues,
} from "features/contracting/helpers/contractingFormHelper";
import ContractingStepSummaryProps from "features/contracting/models/ContractingStepSummaryProps";
import CustomerDataStep from "features/contracting/models/steps/customerData/CustomerDataStep";
import CustomerTypeStep from "features/contracting/models/steps/customerType/CustomerTypeStep";
import PageCard from "features/shared/components/card/PageCard";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import React from "react";
import { useTranslation } from "react-i18next";
import ContractingAddressSummary from "features/contracting/components/overview/summary/ContractingAddressSummary";
import CheckValueInfoItem from "features/shared/components/infoItem/CheckValueInfoItem";
import _ from "lodash";
import { isBusinessType } from "features/contracting/sections/contractingForms/customerDataForm/customerDataFormHelper";
import BusinessPersonBase from "features/contracting/models/steps/customerData/BusinessPersonBase";
import { useTranslationWithPrefix } from "features/shared/hooks/useTranslationWithPrefix";

const CustomerDataSummary = ({
  contracting,
  step,
  configuration,
}: ContractingStepSummaryProps) => {
  // translations
  const tBool = useTranslation("shared\\enums\\boolEnum").t;
  const tAddress = useTranslation(
    "contracting\\enums\\contractingAddressType"
  ).t;
  const tLabels = useTranslationWithPrefix("contracting\\contracting", "customerDataSummary");

  // private props
  const values = getTypedStepValues<CustomerDataStep>(
    contracting,
    step.component
  );
  const customerType = getTypedStepValues<CustomerTypeStep>(
    contracting,
    ContractingFormType.CustomerType
  ).customerType!;

  // helpers
  const getMobile = (
    prefix?: string | undefined,
    mobileNumber?: string | undefined
  ) => {
    if (prefix && mobileNumber) {
      return `${prefix}${mobileNumber}`;
    }
  };

  const getMailMethod = (value: string) => {
    const method = configuration.mailMethodTypes?.find((x) => x.code === value);
    return method ? method.name! : "-";
  };

  const getEmails = () => {
    if (values.mailMethodEmails && values.mailMethodEmails.length > 0) {
      return values.mailMethodEmails.join(", ");
    }
  };

  const isCorrespondenceAddress = () => {
    return values.correspondenceAddressIsSameAsMain === false;
  };

  const isEmailMethod = () => {
    return values.mailMethodCode === "Email";
  };

  const hasContactPerson = () => { 
    return values.contactPersonIsSameAsAuthorizedAgent === false;
  }

  // sub-renders
  const renderBussinesPersonSummary = (
    person: BusinessPersonBase | undefined
  ) => {
    return (
      <>
        <CheckValueInfoItem
          title={tLabels("beforeNameDegree")}
          value={person?.beforeNameDegree}
        />
        <CheckValueInfoItem title={tLabels("firstName")} value={person?.firstName} />
        <CheckValueInfoItem title={tLabels("surname")} value={person?.surname} />
        <CheckValueInfoItem
          title={tLabels("afterNameDegree")}
          value={person?.afterNameDegree}
        />
        <CheckValueInfoItem title={tLabels("email")} value={person?.email} />
        <CheckValueInfoItem
          title={tLabels("mobileNumber")}
          value={getMobile(person?.mobilePrefix, person?.mobileNumber)}
        />
      </>
    );
  };

  // render
  return (
    <div>
      <RenderWrapper isVisible={() => customerType === "Person"}>
        <PageCard title={tLabels("householdCardTitle")} bodyClassName="px-4 py-3">
          <CheckValueInfoItem
            title={tLabels("beforeNameDegree")}
            value={values.beforeNameDegree}
          />
          <CheckValueInfoItem title={tLabels("firstName")} value={values.firstName} />
          <CheckValueInfoItem title={tLabels("surname")} value={values.surname} />
          <CheckValueInfoItem
            title={tLabels("afterNameDegree")}
            value={values.afterNameDegree}
          />
          <CheckValueInfoItem
            title={tLabels("dateOfBirth")}
            value={values.dateOfBirth}
            formatValue={getFormattedDate}
          />
        </PageCard>
      </RenderWrapper>

      <RenderWrapper isVisible={() => isBusinessType(customerType)}>
        <PageCard title={tLabels("businessCardTitle")} bodyClassName="px-4 py-3">
          <CheckValueInfoItem
            title={tLabels("companyRegistrationNumber")}
            value={values.companyRegistrationNumber}
          />
          <CheckValueInfoItem title={tLabels("taxId")} value={values.taxId} />
          <CheckValueInfoItem
            title={tLabels("businessName")}
            value={values.businessName}
          />
        </PageCard>
      </RenderWrapper>

      <PageCard title={tLabels("customerContactCardTitle")} bodyClassName="px-4 py-3">
        <CheckValueInfoItem title={tLabels("email")} value={values.email} />
        <CheckValueInfoItem
          title={tLabels("mobileNumber")}
          value={getMobile(values.mobilePrefix, values.mobileNumber)}
        />
        <RenderWrapper isVisible={() => isBusinessType(customerType)}>
          <CheckValueInfoItem title={tLabels("phoneNumber")} value={values.phoneNumber} />
        </RenderWrapper>
        <CheckValueInfoItem title={tLabels("dataBox")} value={values.dataBox} />
      </PageCard>

      <RenderWrapper isVisible={() => isBusinessType(customerType)}>
        <PageCard title={tLabels("bussinesPersonCardTitle")} bodyClassName="px-4 py-3">
          {renderBussinesPersonSummary(values.authorizedAgent)}
        </PageCard>

        <PageCard title={tLabels("contactPersonCardTitle")} bodyClassName="px-4 py-3">
          <CheckValueInfoItem
            title={tLabels("contactPersonIsSameAsAuthorizedAgent")}
            value={values.contactPersonIsSameAsAuthorizedAgent}
            formatValue={(x) => getBoolText(x, tBool)}
            isEmpty={_.isUndefined}
          />
          <RenderWrapper isVisible={hasContactPerson}>
            {renderBussinesPersonSummary(values.contactPerson)}
        </RenderWrapper>
        </PageCard>
      </RenderWrapper>

      <PageCard
        title={getContractingAddressName(contracting, tAddress)}
        bodyClassName="px-4 py-3"
      >
        <ContractingAddressSummary address={values.mainAddress ?? {}} />
      </PageCard>

      <PageCard title={tLabels("correspondenceAddressCardTitle")} bodyClassName="px-4 py-3">
        <CheckValueInfoItem
          title={getContractingAddressName(
            contracting,
            tAddress,
            true,
            tLabels("correspondenceAddressIsSameAsMainPrefix")
          )}
          value={values.correspondenceAddressIsSameAsMain}
          formatValue={(x) => getBoolText(x, tBool)}
          isEmpty={_.isUndefined}
        />
        <RenderWrapper isVisible={isCorrespondenceAddress}>
          <ContractingAddressSummary
            address={values.correspondenceAddress ?? {}}
          />
        </RenderWrapper>
      </PageCard>

      <PageCard title={tLabels("mailMethodCardTitle")} bodyClassName="px-4 py-3">
        <CheckValueInfoItem
          title={tLabels("mailMethodCode")}
          value={values.mailMethodCode}
          formatValue={getMailMethod}
        />
        <RenderWrapper isVisible={isEmailMethod}>
          <CheckValueInfoItem
            title={tLabels("mailMethodEmails")}
            value={getEmails()}
          />
        </RenderWrapper>
      </PageCard>
    </div>
  );
};

export default CustomerDataSummary;
